import { Api, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutLoadTenderReplyProfile } from "./autogen/AutogenLayoutLoadTenderReplyProfile";
import { EDITemplateSlideout, EDITemplateUpdateProps } from "./EDITemplateSlideout";
import { createCustomizationSlideout, handleMasterBatchCheck, handleButtonMasterBatchClick, setReplyTemplateValues } from "./EdiUtility";
import { ClickEvent, DataSourceModeChangeEvent, DataSourceMode, LookupModelSearchEvent, LookupModelSelectionEvent, ChangeEvent, Checkbox, Layout, TableRowCreationEvent, TableRow, DataHeaderProps, CrudDecorator, CrudDecoratorCloseEvent, Textbox } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { EdiRequiredField } from "./EdiRequiredField";
import { CrudDecoratorProps } from "@mcleod/components/src/page/decorators/CrudDecoratorProps";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { handleCommIdSelection } from "./DataFusionUtil";

export class LoadTenderReplyProfile extends AutogenLayoutLoadTenderReplyProfile {

    private OUTBOUND = "O";
    private INBOUND = "I";
    private REPLY = "R";
    private _programmaticChange: boolean = false;


    override onLoad() {
        this.tableRequiredField.onEditLayoutLoaded = (decorator, tableRow) => {
            const reqRulesLayout = decorator.layout as EdiRequiredField;
            reqRulesLayout.transactionType = "990";
            reqRulesLayout.isGroupRule = false;
            decorator.labelTitle.text = "Auto Create/Reply Conditional Rules";

            const superOnSave = decorator.onSave;
            decorator.onSave = (updatedRow) => {
                if (decorator.layout.mainDataSource.validate()) {
                    const rules = reqRulesLayout.tableEdiDataRule?.data;
                    updatedRow.set('data_rules', rules);
                    superOnSave(updatedRow);
                }
            };
        };

        // Set up event handlers for when table rows are created or displayed
        this.tableRequiredField.addRowCreateListener((event) => {
            // This fires when a new row is being added to the table
            const tableRow = event.getTableRow();
            if (tableRow.table.editLayout) {
                // Override the edit layout loading process
                const originalEditLayoutLoaded = tableRow.table.onEditLayoutLoaded;
                tableRow.table.onEditLayoutLoaded = (decorator, row) => {
                    const layout = decorator.layout;
                    if (layout instanceof EdiRequiredField) {
                        // Set the transaction type to "R" for Reply when opened from LoadTenderReplyProfile
                        layout.transactionType = "990";
                        layout.version = this.mainDataSource.activeRow?.get("reply_version");
                        layout.isGroupRule = false;
                        // You can set other variables here as needed
                        // For example, you could pass partner information:
                        // layout.someOtherProperty = this.mainDataSource.activeRow.get("some_field");
                    }

                    // Call the original handler if it exists
                    if (originalEditLayoutLoaded) {
                        originalEditLayoutLoaded(decorator, row);
                    }
                };
            }
        });
        this.tableRequiredField.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            // Force the open type to be SLIDEOUT
            const layoutName = "lme/datafusion/EdiRequiredField";
            if (layoutName == null) {
                return;
            }
            const layout = Layout.getLayout(layoutName) as EdiRequiredField;
            layout.version = this.mainDataSource.activeRow?.get("reply_version");
            layout.transactionType = "990";
            layout.isGroupRule = false;
            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    title: "Auto Create/Reply Conditional Rules",
                    doAfterClose: (event: CrudDecoratorCloseEvent) => (event) => { },
                    ...dataHeaderProps,
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        };
    }
    setupButtonOnClick() {
        this.setupOnClick("reply_tid", (returnedValues) => {
            this.updateFromTemplate(returnedValues);
        });
    }

    private setupOnClick(fieldName: string, callback: (returnedValues: EDITemplateUpdateProps) => void) {
        EDITemplateSlideout.showSlideout({
            templateId: this.mainDataSource.activeRow.get(fieldName), doAfterSlideOut:
                (returnedValues: EDITemplateUpdateProps) => {
                    callback(returnedValues);
                }
        });
    }

    updateFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxTemplateId.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource.activeRow.get("reply_tid"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource.activeRow.setLookupModelData("reply_tid", lookupModelRow);
        this.textboxTemplateId.displayData(this.mainDataSource.activeRow, null, 0)
        this.mainDataSource.activeRow.set("reply_version", updatedValues.version);
        if (this.mainDataSource.activeRow.get("direction") === this.OUTBOUND) {
            this.mainDataSource.activeRow.set("reply_partner_id", updatedValues.appSenderId);
            this.mainDataSource.activeRow.set("reply_altpartnerid", updatedValues.senderId);
        } else {
            this.mainDataSource.activeRow.set("reply_partner_id", updatedValues.appReceiverId);
            this.mainDataSource.activeRow.set("reply_altpartnerid", updatedValues.receiverId);
        }
    }

    private async loadTemplateData() {
        await Promise.all([
            this.sourceEdiTemplate.search({ id: this.mainDataSource.activeRow.get("reply_tid") })
        ]);
    }

    async afterExecution(event: DataSourceModeChangeEvent) {
        await this.loadTemplateData();
        this.setNextBatchFields();
        // Handle mode change event - check if we're moving to UPDATE mode
        if (event && event.newMode === DataSourceMode.UPDATE && !StringUtil.isEmptyString(this.textboxTemplateId.text)) {
            this.buttonSetup.enabled = true;
        }
    }
    private setNextBatchFields() {
        if (!this.checkboxMasterBatch.checked) {
            this.textboxNextBatch.visible = true;
        } else {
            this.textboxNextBatch.visible = false;
        }
    }

    async templateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            if (template) {
                setReplyTemplateValues(template, this.mainDataSource);
            }
        }
    }

    communicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.direction_input = (this.mainDataSource.activeRow.get("direction") == this.INBOUND) ? this.OUTBOUND : this.INBOUND;
        event.filter.type = this.REPLY;
    }

    templateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.direction = (this.mainDataSource.activeRow.get("direction") == this.INBOUND) ? this.OUTBOUND : this.INBOUND;
        event.filter.transaction_type = this.REPLY;
    }

    /** This is an event handler for the onChange event of checkboxRequiresReply. */
    checkboxRequiresReplyOnChange(event: ChangeEvent) {
        // If this change was initiated programmatically by validateRequiresReply, don't sync other checkboxes
        if (this._programmaticChange) {
            this._programmaticChange = false;
            return;
        }

        const checkReqReply = event.target as Checkbox;

        // Set programmatic change flag to prevent recursive calls from validateRequiresReply
        this._programmaticChange = true;

        // Update all three checkboxes to match the main checkbox
        this.checkboxOriginalRequiresReply.checked = checkReqReply.checked;
        this.checkboxChangeRequiresReply.checked = checkReqReply.checked;
        this.checkboxCancelRequiresReply.checked = checkReqReply.checked;

        // Reset flag after all updates are complete
        this._programmaticChange = false;

    }

    /** This is an event handler for the onChange event of checkboxAutoCreateReply. */
    checkboxAutoCreateReplyOnChange(event: ChangeEvent) {
        const check = event.target as Checkbox;
        if (event.userInitiatedChange) {
            if (check.checked) {
                this.checkboxAutoSendReply.checked = true;
                this.checkboxAutoCreateOrder.checked = true;
            } else {
                if (this.checkboxAutoCreateOrder.checked) {
                    CommonDialogs.showYesNo("Auto Create Order is enabled, as well as Requires Reply.  Are you sure you want to disable Auto Create Reply for this trading partner?",
                        "Auto Create Order").then(result => {
                            if (!result) {
                                check.checked = true;
                            }
                        });
                }
            }
        }
    }

    /** This is an event handler for the onChange event of checkboxAutoSendReply. */
    checkboxAutoSendReplyOnChange(event: ChangeEvent) {
        const check = event.target as Checkbox;
        if (event.userInitiatedChange) {
            if (check.checked === false && this.checkboxAutoCreateReply.checked && this.checkboxRequiresReply.checked) {
                CommonDialogs.showYesNo("Auto Create Reply is enabled, as well as Requires Reply. Are you sure you want to disable Auto Send Reply?", "Auto Send Reply").then(result => {
                    if (result) {
                        this.checkboxAutoSendReply.checked = false;
                    } else {
                        this.checkboxAutoSendReply.checked = true;
                    }
                });
            }
        }
    }

    /** This is an event handler for the onChange event of checkboxMasterBatch. */
    async checkboxMasterBatchOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const check = event.target as Checkbox;
            const partnerName = this.mainDataSource.activeRow.get("partner_name");
            const partnerID = this.mainDataSource.activeRow.get("partner_id");
            const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
            const version = this.mainDataSource.activeRow.get("version");

            await handleMasterBatchCheck(check, partnerName, partnerID, altPartnerId, version);
        }
    }

    /** This is an event handler for the onChange event of checkboxOriginalRequiresReply. */
    checkboxOriginalRequiresReplyOnChange(event: ChangeEvent) {
        // Skip validation if we're in a programmatic change from the main checkbox
        if (this._programmaticChange) {
            return;
        }
        this.validateRequiresReply(event);
    }
    validateRequiresReply(event: ChangeEvent) {
        // Don't validate if we're in the middle of a programmatic change
        if (this._programmaticChange) {
            return;
        }

        const originalChecked = this.checkboxOriginalRequiresReply.checked;
        const changeChecked = this.checkboxChangeRequiresReply.checked;
        const cancelChecked = this.checkboxCancelRequiresReply.checked;

        if (!originalChecked && !changeChecked && !cancelChecked) {
            this._programmaticChange = true;
            this.checkboxRequiresReply.checked = false;
            this._programmaticChange = false;
        } else {
            event.stopImmediatePropagation();
            this._programmaticChange = true;
            this.checkboxRequiresReply.checked = true;
            this._programmaticChange = false;
        }

    }    /** This is an event handler for the onChange event of checkboxChangeRequiresReply. */
    checkboxChangeRequiresReplyOnChange(event: ChangeEvent) {
        // Skip validation if we're in a programmatic change from the main checkbox
        if (this._programmaticChange) {
            return;
        }
        this.validateRequiresReply(event);
    }

    /** This is an event handler for the onChange event of checkboxCancelRequiresReply. */
    checkboxCancelRequiresReplyOnChange(event: ChangeEvent) {
        // Skip validation if we're in a programmatic change from the main checkbox
        if (this._programmaticChange) {
            return;
        }
        this.validateRequiresReply(event);
    }

    /** This is an event handler for the onClick event of buttonViewMasterBatch. */
    async buttonViewMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("reply_partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("reply_altpartnerid");
        const version = this.mainDataSource.activeRow.get("reply_version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxMasterBatch.checked = true;
        });
    }

    /** This is an event handler for the onChange event of checkboxAutoCreateOrder. */
    checkboxAutoCreateOrderOnChange(event: ChangeEvent) {
        const check = event.target as Checkbox;
        if (event.userInitiatedChange) {
            if (check.checked) {
                this.checkboxAutoSendReply.checked = true;
                this.checkboxAutoCreateReply.checked = true;
            }
        }
    }

    /** This is an event handler for the onRowCreate event of tableRequiredField. */
    tableRequiredFieldOnRowCreate(event: TableRowCreationEvent) {
        const partnerId = this.mainDataSource?.activeRow?.get("reply_partner_id");
        const altPartnerId = this.mainDataSource?.activeRow?.get("reply_altpartnerid");
        const version = this.mainDataSource?.activeRow?.get("reply_version");
        const row = event.target as TableRow;
        row.data.set("partner_id", partnerId);
        row.data.set("alt_partner_id", altPartnerId);
        row.data.set("version", version);
        row.data.set("transaction_set", "990");
        row.data.set("direction", "O");
    }

    /** This is an event handler for the onClick event of buttonCustomizeFilename. */
    buttonCustomizeFilenameOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Reply Filename",
            this.mainDataSource,
            "reply_filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("reply_next_batch") ?? 0
        );
    }

    /** This is an event handler for the onClick event of buttonCustomizeTransCtrlNo. */
    buttonCustomizeTransCtrlNoOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomTransControl",
            "Customize Reply Transmission Control Number",
            this.mainDataSource,
            "reply_st02_format",
            "mask",
            false,
            this.mainDataSource?.activeRow?.get("reply_next_batch") ?? 0
        );
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCommId. */
    commIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        if (this.mainDataSource?.activeRow.get("direction") === "I" ){
            handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "990");
        }
    }

/** This is an event handler for the onChange event of textboxTemplateId. */
  textboxTemplateIdOnChange(event: ChangeEvent) {
    this.buttonSetup.enabled = !StringUtil.isEmptyString(this.textboxTemplateId.text);
  }
}
